/**
 * Role scanner — file-system layout, listing, creation, and frontmatter
 * editing for role `.md` files.
 *
 * Layout:
 *
 *   scope="global"  → ~/.appostle/.roles/<category>/<name>.md
 *   scope="project" → <workspaceRoot>/.roles/<category>/<name>.md
 *
 * Files may also live flat directly in `.roles/` (no category subfolder), in
 * which case `category` is `null`.
 *
 * The Roles modal lists/creates/edits files inside these roots ONLY. Any
 * absolute path passed to `writeRoleFrontmatter` is verified to live
 * under one of the roots above before any disk write happens.
 *
 * Frontmatter keys owned by this scanner:
 *   description, trigger-words, allowed-tools, provider, mode
 */

import type { Dirent } from "node:fs";
import fs from "node:fs/promises";
import os from "node:os";
import path from "node:path";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RoleScope = "global" | "project";

export interface RoleEntry {
  /** Stable id — `${scope}:${category}/${name}` or `${scope}:${name}` for flat. */
  id: string;
  scope: RoleScope;
  /** Subdirectory name inside `.roles/`, or null for flat files. */
  category: string | null;
  /** Filename without `.md`. */
  name: string;
  /** First-pass description from frontmatter. Empty string if missing. */
  description: string;
  /** Trigger words from frontmatter. */
  triggerWords: string[];
  /** Frontmatter `provider` field (e.g. "claude/opus"). Empty string if missing. */
  provider: string;
  /** Frontmatter `model` field. Empty string if missing. */
  model: string;
  /** Frontmatter `mode` field (e.g. "read-only" / "default"). Empty string if missing. */
  mode: string;
  /** Absolute path to the .md file. */
  path: string;
  modifiedAt: string;
  size: number;
}

/** User-editable frontmatter fields. Unknown keys are preserved by `writeRoleFrontmatter`. */
export interface RoleFrontmatter {
  name?: string;
  category?: string;
  description?: string;
  triggerWords?: string[];
  allowedTools?: string[];
  provider?: string;
  model?: string;
  mode?: string;
}

export interface ListRolesArgs {
  /** When provided, list only this scope. When absent, merge both (project shadows global). */
  scope?: RoleScope;
  workspaceRoot?: string;
}

export interface CreateRoleArgs {
  scope: RoleScope;
  workspaceRoot?: string;
  /** Kebab-safe name. No path separators, no leading dots. */
  name: string;
  /** Optional category subfolder. Kebab-safe. No path separators. */
  category?: string;
}

export interface CreateRoleResult {
  /** Absolute path to the created <name>.md. */
  path: string;
}

export interface WriteRoleFrontmatterArgs {
  /** Absolute path. Must resolve inside an allowlisted role root. */
  path: string;
  frontmatter: RoleFrontmatter;
}

export interface MoveRoleArgs {
  /** Current absolute path. */
  path: string;
  /** New kebab-safe name (without .md). Omit to keep the current name. */
  newName?: string;
  /** New category subfolder. Empty string = flat (no category). Omit to keep current. */
  newCategory?: string;
}

export interface MoveRoleResult {
  /** New absolute path after the move. */
  path: string;
}

// ---------------------------------------------------------------------------
// Path resolution
// ---------------------------------------------------------------------------

const NAME_REGEX = /^[a-z0-9][a-z0-9._-]*$/i;

function homeDir(): string {
  return process.env.HOME || os.homedir();
}

function resolveScopeDir(scope: RoleScope, workspaceRoot?: string): string {
  if (scope === "project") {
    if (!workspaceRoot) {
      throw new Error('workspaceRoot is required for scope "project"');
    }
    return path.join(workspaceRoot, ".roles");
  }
  return path.join(homeDir(), ".appostle", ".roles");
}

function allowedRoots(workspaceRoot?: string): string[] {
  const roots: string[] = [path.join(homeDir(), ".appostle", ".roles")];
  if (workspaceRoot) {
    roots.push(path.join(workspaceRoot, ".roles"));
  }
  return roots.map((r) => path.resolve(r));
}

function isInsideAllowedRoot(absPath: string, workspaceRoot?: string): boolean {
  const resolved = path.resolve(absPath);
  for (const root of allowedRoots(workspaceRoot)) {
    const rel = path.relative(root, resolved);
    if (rel === "" || (!rel.startsWith("..") && !path.isAbsolute(rel))) {
      return true;
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// Frontmatter parser/emitter — narrow YAML, preserves unknown keys verbatim
// ---------------------------------------------------------------------------

interface ParsedRoleFile {
  rawFrontmatterLines: string[];
  body: string;
  hadFrontmatter: boolean;
}

const FRONTMATTER_RE = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/;

function parseRoleFile(text: string): ParsedRoleFile {
  const match = FRONTMATTER_RE.exec(text);
  if (!match) {
    return { rawFrontmatterLines: [], body: text, hadFrontmatter: false };
  }
  const yamlBlock = match[1] ?? "";
  const body = match[2] ?? "";
  return {
    rawFrontmatterLines: yamlBlock.split(/\r?\n/),
    body,
    hadFrontmatter: true,
  };
}

function unquote(value: string): string {
  if (
    (value.startsWith('"') && value.endsWith('"')) ||
    (value.startsWith("'") && value.endsWith("'"))
  ) {
    return value.slice(1, -1);
  }
  return value;
}

interface ParsedFrontmatter {
  description: string;
  triggerWords: string[];
  allowedTools: string[];
  provider: string;
  model: string;
  mode: string;
}

function parseFrontmatter(text: string): ParsedFrontmatter {
  const empty: ParsedFrontmatter = {
    description: "",
    triggerWords: [],
    allowedTools: [],
    provider: "",
    model: "",
    mode: "",
  };
  const { rawFrontmatterLines: lines, hadFrontmatter } = parseRoleFile(text);
  if (!hadFrontmatter) return empty;

  const out: ParsedFrontmatter = { ...empty };
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    const m = /^([A-Za-z][A-Za-z0-9_-]*)\s*:\s*(.*)$/.exec(line);
    if (!m) {
      i++;
      continue;
    }
    const key = m[1];
    const value = unquote((m[2] ?? "").trim());
    if (key === "description") {
      out.description = value;
    } else if (key === "provider") {
      out.provider = value;
    } else if (key === "model") {
      out.model = value;
    } else if (key === "mode") {
      out.mode = value;
    } else if (key === "trigger-words" || key === "allowed-tools") {
      const target = key === "trigger-words" ? "triggerWords" : "allowedTools";
      if (value) {
        out[target] = value
          .replace(/^\[|\]$/g, "")
          .split(",")
          .map((s) => unquote(s.trim()))
          .filter((s) => s.length > 0);
      } else {
        const items: string[] = [];
        let j = i + 1;
        while (j < lines.length) {
          const next = lines[j] ?? "";
          const itemMatch = /^\s+-\s+(.+)$/.exec(next);
          if (!itemMatch) break;
          items.push(unquote(itemMatch[1].trim()));
          j++;
        }
        out[target] = items;
        i = j;
        continue;
      }
    }
    i++;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Owned-key spans + rewrite (same pattern as skills/scanner.ts)
// ---------------------------------------------------------------------------

type OwnedKey =
  | "name"
  | "category"
  | "description"
  | "trigger-words"
  | "allowed-tools"
  | "provider"
  | "model"
  | "mode";
const OWNED_KEYS: OwnedKey[] = [
  "name",
  "category",
  "description",
  "trigger-words",
  "allowed-tools",
  "provider",
  "model",
  "mode",
];

interface FrontmatterSpan {
  key: OwnedKey;
  startLine: number;
  endLine: number;
}

function findOwnedSpans(lines: string[]): FrontmatterSpan[] {
  const spans: FrontmatterSpan[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    const keyMatch = /^([A-Za-z][A-Za-z0-9_-]*)\s*:\s*(.*)$/.exec(line);
    if (!keyMatch) {
      i++;
      continue;
    }
    const key = keyMatch[1] as OwnedKey;
    const value = keyMatch[2];
    if (!OWNED_KEYS.includes(key)) {
      i++;
      continue;
    }
    let end = i + 1;
    if (value === "") {
      while (end < lines.length) {
        const next = lines[end] ?? "";
        if (/^\s+-\s+/.test(next) || /^\s*$/.test(next)) {
          end++;
        } else {
          break;
        }
      }
    }
    spans.push({ key, startLine: i, endLine: end });
    i = end;
  }
  return spans;
}

function emitFrontmatterValue(key: OwnedKey, value: string | string[]): string[] {
  if (key === "trigger-words" || key === "allowed-tools") {
    if (!Array.isArray(value) || value.length === 0) return [];
    return [`${key}:`, ...value.map((v) => `  - ${v}`)];
  }
  const text = typeof value === "string" ? value : "";
  if (text.length === 0) return [];
  const needsQuote = /[:#\n]/.test(text) || text.startsWith(" ") || text.endsWith(" ");
  const formatted = needsQuote ? `"${text.replace(/"/g, '\\"')}"` : text;
  return [`${key}: ${formatted}`];
}

function rewriteFrontmatter(originalLines: string[], next: RoleFrontmatter): string[] {
  const spans = findOwnedSpans(originalLines);
  const ownedKeys = new Set(spans.map((s) => s.key));

  const replacements = new Map<OwnedKey, string[]>();
  if (next.name !== undefined) {
    replacements.set("name", emitFrontmatterValue("name", next.name));
  }
  if (next.category !== undefined) {
    replacements.set("category", emitFrontmatterValue("category", next.category));
  }
  if (next.description !== undefined) {
    replacements.set("description", emitFrontmatterValue("description", next.description));
  }
  if (next.triggerWords !== undefined) {
    replacements.set("trigger-words", emitFrontmatterValue("trigger-words", next.triggerWords));
  }
  if (next.allowedTools !== undefined) {
    replacements.set("allowed-tools", emitFrontmatterValue("allowed-tools", next.allowedTools));
  }
  if (next.provider !== undefined) {
    replacements.set("provider", emitFrontmatterValue("provider", next.provider));
  }
  if (next.model !== undefined) {
    replacements.set("model", emitFrontmatterValue("model", next.model));
  }
  if (next.mode !== undefined) {
    replacements.set("mode", emitFrontmatterValue("mode", next.mode));
  }

  const out: string[] = [];
  let i = 0;
  while (i < originalLines.length) {
    const span = spans.find((s) => s.startLine === i);
    if (span) {
      if (replacements.has(span.key)) {
        out.push(...(replacements.get(span.key) ?? []));
      } else {
        for (let j = span.startLine; j < span.endLine; j++) {
          out.push(originalLines[j] ?? "");
        }
      }
      i = span.endLine;
      continue;
    }
    out.push(originalLines[i] ?? "");
    i++;
  }

  for (const key of OWNED_KEYS) {
    if (replacements.has(key) && !ownedKeys.has(key)) {
      out.push(...(replacements.get(key) ?? []));
    }
  }

  return out;
}

// ---------------------------------------------------------------------------
// readRolesFromDir (internal helper — reads one flat directory)
// ---------------------------------------------------------------------------

async function readRolesFromDir(
  scope: RoleScope,
  dir: string,
  category: string | null,
): Promise<RoleEntry[]> {
  let entries: Dirent[];
  try {
    entries = await fs.readdir(dir, { withFileTypes: true });
  } catch {
    return [];
  }
  const results: RoleEntry[] = [];
  for (const entry of entries) {
    if (!entry.isFile()) continue;
    if (!entry.name.endsWith(".md")) continue;
    const name = entry.name.slice(0, -".md".length);
    if (!name || !NAME_REGEX.test(name)) continue;
    const fullPath = path.join(dir, entry.name);
    let stat: { mtime: Date; size: number };
    try {
      const s = await fs.stat(fullPath);
      stat = { mtime: s.mtime, size: s.size };
    } catch {
      continue;
    }
    let parsed: ParsedFrontmatter;
    try {
      const text = await fs.readFile(fullPath, "utf8");
      parsed = parseFrontmatter(text);
    } catch {
      parsed = {
        description: "",
        triggerWords: [],
        allowedTools: [],
        provider: "",
        model: "",
        mode: "",
      };
    }
    const id = category ? `${scope}:${category}/${name}` : `${scope}:${name}`;
    results.push({
      id,
      scope,
      category,
      name,
      description: parsed.description,
      triggerWords: parsed.triggerWords,
      provider: parsed.provider,
      model: parsed.model,
      mode: parsed.mode,
      path: fullPath,
      modifiedAt: stat.mtime.toISOString(),
      size: stat.size,
    });
  }
  results.sort((a, b) => a.name.localeCompare(b.name));
  return results;
}

// ---------------------------------------------------------------------------
// readRolesFromScopeDir — scans flat files + one level of category subdirs
// ---------------------------------------------------------------------------

async function readRolesFromScopeDir(scope: RoleScope, scopeDir: string): Promise<RoleEntry[]> {
  let topEntries: Dirent[];
  try {
    topEntries = await fs.readdir(scopeDir, { withFileTypes: true });
  } catch {
    return [];
  }

  // Flat files directly in .roles/
  const flat = await readRolesFromDir(scope, scopeDir, null);

  // Category subdirectories
  const categoryResults = await Promise.all(
    topEntries
      .filter((e) => e.isDirectory() && NAME_REGEX.test(e.name))
      .map((e) => readRolesFromDir(scope, path.join(scopeDir, e.name), e.name)),
  );

  const all = [...flat, ...categoryResults.flat()];
  all.sort((a, b) => {
    // Sort by category first, then name
    const catA = a.category ?? "";
    const catB = b.category ?? "";
    if (catA !== catB) return catA.localeCompare(catB);
    return a.name.localeCompare(b.name);
  });
  return all;
}

// ---------------------------------------------------------------------------
// listRoles
// ---------------------------------------------------------------------------

/**
 * Lists role files.
 *
 * - When `scope` is provided, lists only that scope.
 * - When `scope` is absent (legacy call), merges both scopes — project roles
 *   shadow global roles on id collision.
 */
export async function listRoles(args: ListRolesArgs): Promise<RoleEntry[]> {
  if (args.scope === "project") {
    return readRolesFromScopeDir("project", resolveScopeDir("project", args.workspaceRoot));
  }
  if (args.scope === "global") {
    return readRolesFromScopeDir("global", resolveScopeDir("global"));
  }
  // Legacy merged call
  const project = args.workspaceRoot
    ? await readRolesFromScopeDir("project", resolveScopeDir("project", args.workspaceRoot))
    : [];
  const global = await readRolesFromScopeDir("global", resolveScopeDir("global"));
  const projectIds = new Set(project.map((r) => r.id));
  const merged = [...project, ...global.filter((r) => !projectIds.has(r.id))];
  merged.sort((a, b) => {
    const catA = a.category ?? "";
    const catB = b.category ?? "";
    if (catA !== catB) return catA.localeCompare(catB);
    return a.name.localeCompare(b.name);
  });
  return merged;
}

// ---------------------------------------------------------------------------
// resolveRoleBody — find a role by name and return its markdown body
// ---------------------------------------------------------------------------

export interface ResolvedRole {
  body: string;
  allowedTools: string[];
}

export async function resolveRole(args: {
  roleName: string;
  workspaceRoot?: string;
}): Promise<ResolvedRole | null> {
  const roles = await listRoles({ workspaceRoot: args.workspaceRoot });
  const match = roles.find((r) => r.name === args.roleName);
  if (!match) return null;
  try {
    const text = await fs.readFile(match.path, "utf8");
    const parsed = parseRoleFile(text);
    const fm = parseFrontmatter(text);
    const body = parsed.body.trim();
    if (!body && fm.allowedTools.length === 0) return null;
    return { body, allowedTools: fm.allowedTools };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// createRole
// ---------------------------------------------------------------------------

export async function createRole(args: CreateRoleArgs): Promise<CreateRoleResult> {
  if (!NAME_REGEX.test(args.name)) {
    throw new Error(
      `Invalid role name "${args.name}". Use letters, digits, dot, underscore, dash.`,
    );
  }
  if (args.name.includes("/") || args.name.includes("\\") || args.name.includes("..")) {
    throw new Error(`Role name must not contain path separators or "..".`);
  }

  const scopeDir = resolveScopeDir(args.scope, args.workspaceRoot);
  const dir = args.category ? path.join(scopeDir, args.category) : scopeDir;
  await fs.mkdir(dir, { recursive: true });

  const filePath = path.join(dir, `${args.name}.md`);
  try {
    await fs.access(filePath);
    throw new Error(`A role named "${args.name}" already exists at ${filePath}`);
  } catch (err: unknown) {
    if (
      err &&
      typeof err === "object" &&
      "code" in err &&
      (err as { code: string }).code !== "ENOENT"
    ) {
      throw err;
    }
    if (err instanceof Error && err.message.includes("already exists")) {
      throw err;
    }
  }

  const initial = buildStarterRole(args.name);
  await fs.writeFile(filePath, initial, "utf8");
  return { path: filePath };
}

function buildStarterRole(name: string): string {
  return `---
name: ${name}
description: ""
trigger-words: []
allowed-tools: []
provider: claude/opus
mode: default
---

# ${name}

You are a ${name}. Describe the role's behavior, scope, and any constraints
here. The body of this file is loaded as the agent's system prompt when
the role is invoked.
`;
}

// ---------------------------------------------------------------------------
// writeRoleFrontmatter
// ---------------------------------------------------------------------------

export async function writeRoleFrontmatter(
  args: WriteRoleFrontmatterArgs,
  workspaceRoot?: string,
): Promise<void> {
  if (!path.isAbsolute(args.path)) {
    throw new Error(`writeRoleFrontmatter expects an absolute path; got "${args.path}"`);
  }
  if (!isInsideAllowedRoot(args.path, workspaceRoot)) {
    throw new Error(`Path "${args.path}" is not inside an allowlisted role root`);
  }

  let original: string;
  try {
    original = await fs.readFile(args.path, "utf8");
  } catch (err) {
    throw new Error(
      `Failed to read role file: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  const parsed = parseRoleFile(original);
  const newLines = rewriteFrontmatter(parsed.rawFrontmatterLines, args.frontmatter);
  const nextFrontmatter = ["---", ...newLines, "---"].join("\n");
  const nextContent = parsed.hadFrontmatter
    ? `${nextFrontmatter}\n${parsed.body}`
    : `${nextFrontmatter}\n\n${original}`;

  await fs.writeFile(args.path, nextContent, "utf8");
}

// ---------------------------------------------------------------------------
// moveRole — rename and/or change category (file move + frontmatter update)
// ---------------------------------------------------------------------------

export async function moveRole(
  args: MoveRoleArgs,
  workspaceRoot?: string,
): Promise<MoveRoleResult> {
  if (!path.isAbsolute(args.path)) {
    throw new Error(`moveRole expects an absolute path; got "${args.path}"`);
  }
  if (!isInsideAllowedRoot(args.path, workspaceRoot)) {
    throw new Error(`Path "${args.path}" is not inside an allowlisted role root`);
  }

  const oldDir = path.dirname(args.path);
  const oldFilename = path.basename(args.path, ".md");

  // Determine the .roles/ root this file lives under
  const roots = allowedRoots(workspaceRoot);
  const rolesRoot = roots.find((r) => path.resolve(args.path).startsWith(r));
  if (!rolesRoot) {
    throw new Error(`Cannot determine roles root for "${args.path}"`);
  }

  // Resolve relative path from roles root to figure out current category
  const relFromRoot = path.relative(rolesRoot, path.dirname(args.path));
  const currentCategory = relFromRoot && relFromRoot !== "." ? relFromRoot : "";

  const newName = args.newName ?? oldFilename;
  const newCategory = args.newCategory !== undefined ? args.newCategory : currentCategory;

  if (!NAME_REGEX.test(newName)) {
    throw new Error(`Invalid role name: "${newName}"`);
  }
  if (newCategory && !NAME_REGEX.test(newCategory)) {
    throw new Error(`Invalid category name: "${newCategory}"`);
  }

  const newDir = newCategory ? path.join(rolesRoot, newCategory) : rolesRoot;
  const newPath = path.join(newDir, `${newName}.md`);

  if (path.resolve(newPath) === path.resolve(args.path)) {
    return { path: args.path };
  }

  // Ensure target directory exists
  await fs.mkdir(newDir, { recursive: true });

  // Check target doesn't already exist
  try {
    await fs.access(newPath);
    throw new Error(`A role already exists at "${newPath}"`);
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code !== "ENOENT") throw err;
  }

  // Move the file
  await fs.rename(args.path, newPath);

  // Update frontmatter with new name and category
  const frontmatterPatch: RoleFrontmatter = {};
  if (newName !== oldFilename) {
    frontmatterPatch.description = undefined; // keep as-is
  }
  // Always sync the category field in frontmatter
  await writeRoleFrontmatter(
    {
      path: newPath,
      frontmatter: {
        ...(newName !== oldFilename ? { name: newName } : {}),
      },
    },
    workspaceRoot,
  );

  // Clean up empty old directory (only if it was a category subfolder)
  if (oldDir !== rolesRoot) {
    try {
      const remaining = await fs.readdir(oldDir);
      if (remaining.length === 0) {
        await fs.rmdir(oldDir);
      }
    } catch {
      // Best-effort cleanup
    }
  }

  return { path: newPath };
}
